import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { AppHeaderBar } from "@/components/ui/app-header";
import Link from "@/components/DashboardLink";
import { BarChart3, Bell, Github, House, SettingsIcon } from "lucide-react";
import { isCodesmithPath, isAdminPath, isFixedMainPath, isHomePath, isProofPath, isSandboxesPath, isSettingsPath, isStandaloneAccountPath } from "./sidebar/useActiveApp";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import { BlacksmithLogomarkSVG } from "@repo/blacksmith-ui/blacksmith-logomark";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { SandboxesIcon } from "@/components/sandboxes/SandboxesIcon";
import { RAIL_ROW, RAIL_ROW_ACTIVE } from "@repo/blacksmith-ui/app-rail";
import { RailStatusWidgetSkeleton } from "./sidebar/StatusIndicator";
import { SidebarProvider, SidebarInset } from "@repo/blacksmith-ui/sidebar";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_WIDTH } from "./sidebar/railConstants";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg } from "./sidebar/useCodesmithEnabledCache";

// This ensures the skeleton matches the actual sidebar state even during initial render
export function getSidebarStateFromStorage(): boolean {
  if (typeof window === "undefined") {
    return true; // Default to expanded on SSR
  }
  try {
    const stored = localStorage.getItem("sidebar-open");
    if (stored === null) {
      return true; // Default to expanded if not set
    }
    return stored === "true";
  } catch {
    return true; // Default to expanded on error
  }
}

/**
 * Twin of the mounted rail's nav row: same 32px footprint, icon box, and
 * resting icon color. With an href it is a live Link with the rail's hover
 * and focus affordances, so a click landing during the skeleton window
 * navigates instead of dying on placeholder chrome; without one it stays a
 * plain div that shouldn't advertise interactivity.
 */
function RailSkeletonRow(t0) {
  const $ = _c(15);
  const {
    icon,
    active,
    slot,
    href
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contents = t1;
  if (href) {
    const t2 = active && RAIL_ROW_ACTIVE;
    let t3;
    if ($[2] !== t2) {
      t3 = cn(RAIL_ROW, t2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== contents || $[5] !== href || $[6] !== slot || $[7] !== t3) {
      t4 = <Link href={href} data-slot={slot} className={t3}>{contents}</Link>;
      $[4] = contents;
      $[5] = href;
      $[6] = slot;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const t2 = active && RAIL_ROW_ACTIVE;
  let t3;
  if ($[9] !== t2) {
    t3 = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t2);
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contents || $[12] !== slot || $[13] !== t3) {
    t4 = <div data-slot={slot} className={t3}>{contents}</div>;
    $[11] = contents;
    $[12] = slot;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/**
 * Placeholder matching AppSwitcherRail's slim footprint: logo, org-switcher
 * pill, app rows, and the global footer (three icon rows + status widget), so
 * the rail never sits as a blank strip while user/org data loads. Every fixed
 * glyph is the mounted rail's own icon rather than a gray block: none of them
 * depend on the data being awaited, so a placeholder there would only flash
 * before the identical icon lands. Only the org pill, whose content is the
 * data being loaded, stays a `Skeleton`; the status widget is a static twin at
 * its own pending values (see RailStatusWidgetSkeleton), which keeps the footer
 * rows above it at their final height. The Codesmith and Proof app rows follow
 * the same gate the mounted rail uses, so nothing pops in or out on swap.
 */
export function RailSkeleton() {
  const $ = _c(51);
  const router = useRouter();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const {
    user
  } = useAuth();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  let activeSection;
  let navOrg;
  let showCodesmithRow;
  let t0;
  if ($[0] !== router.pathname || $[1] !== urlInstanceSlug || $[2] !== urlOrg) {
    const org = urlOrg ? {
      orgName: urlOrg,
      instanceSlug: urlInstanceSlug
    } : getCachedLastActiveOrg(urlInstanceSlug);
    const isCodesmithRoute = isCodesmithPath(router.pathname);
    showCodesmithRow = getCachedCodesmithEnabled(org) !== false;
    navOrg = org?.orgName;
    activeSection = router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : isCodesmithRoute ? "codesmith" : isProofPath(router.pathname) ? "proof" : isSandboxesPath(router.pathname) ? "sandboxes" : "actions";
    t0 = cn("h-full shrink-0 bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", isCodesmithRoute && "max-md:hidden");
    $[0] = router.pathname;
    $[1] = urlInstanceSlug;
    $[2] = urlOrg;
    $[3] = activeSection;
    $[4] = navOrg;
    $[5] = showCodesmithRow;
    $[6] = t0;
  } else {
    activeSection = $[3];
    navOrg = $[4];
    showCodesmithRow = $[5];
    t0 = $[6];
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: RAIL_WIDTH
    };
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AppHeaderBar><div className="flex size-8 shrink-0 items-center justify-center"><div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div></div></AppHeaderBar>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="pt-[calc(var(--spacing-app-header-gap)-0.5rem)]"><Skeleton className="h-8 w-full rounded-lg" /></div>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <House className="size-[18px]" />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = activeSection === "home";
  const t6 = navOrg ? `/${navOrg}/home` : undefined;
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <RailSkeletonRow icon={t4} active={t5} href={t6} />;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Github className="size-[18px]" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = activeSection === "actions";
  const t10 = navOrg ? `/${navOrg}/runs/jobs` : undefined;
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <RailSkeletonRow icon={t8} active={t9} href={t10} />;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== activeSection || $[19] !== navOrg || $[20] !== showCodesmithRow) {
    t12 = showCodesmithRow && <RailSkeletonRow icon={<CodesmithIcon />} active={activeSection === "codesmith"} slot="rail-skeleton-codesmith-row" href={navOrg ? `/${navOrg}/sessions/new` : undefined} />;
    $[18] = activeSection;
    $[19] = navOrg;
    $[20] = showCodesmithRow;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== activeSection || $[23] !== navOrg || $[24] !== showCodesmithRow) {
    t13 = showCodesmithRow && <RailSkeletonRow icon={<ProofIconBold className="size-[18px]" />} active={activeSection === "proof"} href={navOrg ? `/${navOrg}/proof` : undefined} />;
    $[22] = activeSection;
    $[23] = navOrg;
    $[24] = showCodesmithRow;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SandboxesIcon className="size-[18px]" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = activeSection === "sandboxes";
  const t16 = navOrg ? `/${navOrg}/sandboxes` : undefined;
  let t17;
  if ($[27] !== t15 || $[28] !== t16) {
    t17 = <RailSkeletonRow icon={t14} active={t15} href={t16} />;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t17 || $[34] !== t7) {
    t18 = <div className="flex flex-col gap-1 pt-1">{t7}{t11}{t12}{t13}{t17}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t17;
    $[34] = t7;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="flex-1" />;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <BarChart3 className="size-[18px]" />;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const t21 = activeSection === "usage";
  let t22;
  if ($[38] !== t21) {
    t22 = <RailSkeletonRow icon={t20} active={t21} />;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} />;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <RailSkeletonRow icon={<Bell className="size-[18px]" />} />;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="pt-1"><RailStatusWidgetSkeleton /></div>;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t22) {
    t26 = <div className="flex flex-col gap-1 pb-2">{t22}{t23}{t24}{t25}</div>;
    $[43] = t22;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t18 || $[46] !== t26) {
    t27 = <div className="flex h-full w-full flex-col px-2">{t2}{t3}{t18}{t19}{t26}</div>;
    $[45] = t18;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t0 || $[49] !== t27) {
    t28 = <div className={t0} style={t1} data-slot="app-switcher-rail-skeleton">{t27}</div>;
    $[48] = t0;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  return t28;
}

/**
 * Full-page loading skeleton: rail + sidebar skeleton + centered logo in main
 * area, or rail + SessionSkeleton on codesmith routes (which have no Actions
 * sidebar; the session sidebar lives inside the main pane at a persisted width).
 * Used during auth checks and pending-auth query failures so loading matches app chrome.
 * AuthProvider only renders this on the client after hydration, so
 * localStorage can be read synchronously on first paint.
 */
export function SidebarLayoutSkeleton() {
  const $ = _c(28);
  const router = useRouter();
  const [sidebarOpen] = useState(getSidebarStateFromStorage);
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCodesmithRoute = t0;
  let t1;
  if ($[2] !== router.pathname) {
    t1 = isStandaloneAccountPath(router.pathname);
    $[2] = router.pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isStandalone = t1;
  let t2;
  if ($[4] !== isCodesmithRoute || $[5] !== isStandalone || $[6] !== router.pathname) {
    t2 = isStandalone ? isSettingsPath(router.pathname) || isAdminPath(router.pathname) : !isCodesmithRoute && !isProofPath(router.pathname) && !isSandboxesPath(router.pathname);
    $[4] = isCodesmithRoute;
    $[5] = isStandalone;
    $[6] = router.pathname;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const showSidebar = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RailSkeleton />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== isStandalone || $[10] !== showSidebar || $[11] !== sidebarOpen) {
    t4 = showSidebar && <SidebarSkeleton collapsed={!sidebarOpen} standalone={isStandalone} />;
    $[9] = isStandalone;
    $[10] = showSidebar;
    $[11] = sidebarOpen;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== router.pathname) {
    t5 = isFixedMainPath(router.pathname) ? "min-h-0 overflow-hidden" : "overflow-y-auto [scrollbar-gutter:stable]";
    $[13] = router.pathname;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = cn("flex-1 bg-transparent!", t5);
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== isCodesmithRoute) {
    t7 = isCodesmithRoute ? <SessionSkeleton /> : <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
    $[17] = isCodesmithRoute;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <SidebarInset className={t6}>{t7}</SidebarInset>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t4 || $[23] !== t8) {
    t9 = <div className="flex-1 flex overflow-hidden">{t3}{t4}{t8}</div>;
    $[22] = t4;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== sidebarOpen || $[26] !== t9) {
    t10 = <div className="flex flex-col h-dvh"><SidebarProvider open={sidebarOpen} className="flex-1 min-h-0">{t9}</SidebarProvider></div>;
    $[25] = sidebarOpen;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}